// Panels „Stammdaten & Kontakt“ und „Mietverhältnis“ (Handoff Mieter §6). Reine Funktionen – testbar.

import { emailGueltig } from '../immobilien/portal';
import type { KontaktKanal } from '../kontakt/kontaktKanaele';
import { anredeKurz } from './detail';
import type { Anrede } from './neuerMieter';

export interface StammdatenEntwurf {
  /** '' = Briefanrede aus den Altdaten bleibt unverändert */
  anrede: Anrede | '';
  vorname: string;
  nachname: string;
  firma: string;
  geburtsdatum: string;
  email: string;
  telefon: string;
  iban: string;
  kontoinhaber: string;
}

const t = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

export function stammdatenAusMieter(m: Record<string, unknown>): StammdatenEntwurf {
  const kurz = anredeKurz(t(m.anrede));
  const name = `${t(m.vorname)} ${t(m.nachname)}`.trim();
  return {
    anrede: kurz === 'Frau' || kurz === 'Herr' || kurz === 'Eheleute' || kurz === 'Divers' ? kurz : '',
    vorname: t(m.vorname),
    nachname: t(m.nachname),
    firma: t(m.firma_name),
    geburtsdatum: t(m.geburtsdatum).slice(0, 10),
    email: t(m.email),
    telefon: t(m.telefon) || t(m.mobil),
    iban: t(m.mieter_iban) || t(m.bankverbindung),
    // Standard = Mietername (README §6)
    kontoinhaber: t(m.mieter_kontoinhaber) || name,
  };
}

export function stammdatenFehler(e: StammdatenEntwurf): Partial<Record<'name' | 'email' | 'iban' | 'geburtsdatum', string>> {
  const f: Partial<Record<'name' | 'email' | 'iban' | 'geburtsdatum', string>> = {};
  if (!e.firma && (!e.vorname || !e.nachname)) f.name = 'Vor- und Nachname sind Pflicht';
  if (e.email && !emailGueltig(e.email)) f.email = 'Bitte eine gültige E-Mail-Adresse eingeben';
  const iban = e.iban.replace(/\s+/g, '');
  if (iban && !/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/i.test(iban)) f.iban = 'IBAN ist unvollständig';
  if (e.geburtsdatum && e.geburtsdatum > new Date().toISOString().slice(0, 10)) f.geburtsdatum = 'Geburtsdatum liegt in der Zukunft';
  return f;
}

/**
 * Update für die mieter-Zeile. Die Anrede wird nur geschrieben, wenn eine gewählt ist – die
 * Briefanreden der Altdaten bleiben sonst erhalten. E-Mail/Telefon laufen über die Kanäle.
 */
export function stammdatenPayload(e: StammdatenEntwurf, vorher: StammdatenEntwurf): Record<string, unknown> {
  const p: Record<string, unknown> = {
    vorname: e.vorname.trim() || null,
    nachname: e.nachname.trim() || null,
    geburtsdatum: e.geburtsdatum || null,
    mieter_iban: e.iban.trim() ? e.iban.trim().replace(/\s+/g, ' ') : null,
  };
  if (e.anrede && e.anrede !== vorher.anrede) p.anrede = e.anrede;
  if (vorher.firma || e.firma) p.firma_name = e.firma.trim() || null;
  const name = `${e.vorname.trim()} ${e.nachname.trim()}`.trim();
  const inhaber = e.kontoinhaber.trim();
  p.mieter_kontoinhaber = inhaber && inhaber !== name ? inhaber : null;
  return p;
}

// Kanal-Helfer liegen seit E5 (Eigentümer) gemeinsam in lib/kontakt/kontaktKanaele.ts
export { kanaeleMitPrimaer, kanaeleAusSpalten } from '../kontakt/kontaktKanaele';

export function stammdatenGeaendert(a: StammdatenEntwurf, b: StammdatenEntwurf): boolean {
  return (Object.keys(a) as (keyof StammdatenEntwurf)[]).some(k => a[k].trim() !== b[k].trim());
}

/** Gesetzliche Kündigungsfrist des Mieters (§ 573c BGB): 3 Monate, sonst aus dem Vertrag. */
export function kuendigungsfristText(vertragMonate: number | null | undefined): string {
  return vertragMonate && vertragMonate > 0 ? `${vertragMonate} ${vertragMonate === 1 ? 'Monat' : 'Monate'}` : '3 Monate (gesetzlich)';
}

/**
 * Vorschlag für das Mietende bei einer Mieterkündigung (§ 573c BGB): Zugang bis zum 3. Werktag →
 * Ablauf des übernächsten Monats, sonst einen Monat später. Werktage ohne Feiertage gezählt.
 */
export function kuendigungZum(eingang: string, fristMonate = 3): string {
  const [j, m, d] = eingang.split('-').map(Number);
  if (!j || !m || !d) return '';
  let werktage = 0;
  let dritterWerktag = 31;
  for (let tag = 1; tag <= 31; tag++) {
    const dt = new Date(Date.UTC(j, m - 1, tag));
    if (dt.getUTCMonth() !== m - 1) break;
    const wt = dt.getUTCDay();
    if (wt !== 0) { werktage += 1; if (werktage === 3) { dritterWerktag = tag; break; } }
  }
  const zusatz = d <= dritterWerktag ? 0 : 1;
  // letzter Tag des Monats (m - 1 + frist - 1 + zusatz)
  const ende = new Date(Date.UTC(j, m - 1 + (fristMonate - 1) + zusatz + 1, 0));
  return ende.toISOString().slice(0, 10);
}

/** Archiviert = Status „ehemalig“ (wie nach einem Mieterwechsel). */
export function istEhemalig(status: string | null | undefined): boolean {
  return (status ?? '').trim().toLowerCase() === 'ehemalig';
}

/** Auszugsdatum fürs Archivieren: vorhandenes Mietende, wenn es schon erreicht ist, sonst heute. */
export function archivVorschlag(mietende: string | null | undefined, heute: string): string {
  const ende = (mietende ?? '').slice(0, 10);
  return ende && ende <= heute ? ende : heute;
}

export function archivFehler(auszug: string, mietbeginn: string | null | undefined, heute: string): string | null {
  if (!auszug) return 'Bitte das Auszugsdatum angeben.';
  if (auszug > heute) return 'Der Auszug liegt in der Zukunft – dafür bitte die Kündigung erfassen.';
  const beginn = (mietbeginn ?? '').slice(0, 10);
  if (beginn && auszug < beginn) return 'Der Auszug liegt vor dem Mietbeginn.';
  return null;
}

export function archivPatch(auszug: string): { status: 'ehemalig'; mietende: string } {
  return { status: 'ehemalig', mietende: auszug };
}

/** Wieder aktivieren: ein bereits erreichtes Mietende wird entfernt, ein künftiges bleibt. */
export function reaktivierenPatch(mietende: string | null | undefined, heute: string): { status: 'aktiv'; mietende: string | null } {
  const ende = (mietende ?? '').slice(0, 10);
  return { status: 'aktiv', mietende: ende && ende > heute ? ende : null };
}
